import { useAuth } from "@clerk/tanstack-react-start";
import { useStore } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";

import { AuthFlow } from "~/components/auth/auth-flow";
import { useAppForm } from "~/components/form/app-form";
import { FieldErrorsProvider } from "~/components/form/fields/field-errors";
import { NOVA_SUBSCRIPTION_QUERY_KEY } from "~/components/nova/use-nova-access";
import {
  READINESS_COMPONENTS_INTRO,
  READINESS_HERO,
  READINESS_STEPS_TITLE,
  ReadinessComponentTiles,
  ReadinessPreviewCard,
  ReadinessSteps,
} from "~/components/readiness/readiness-landing-sections";
import {
  READINESS_RESULTS_VIDEO_QUERY,
  ReadinessNovaOffer,
  ReadinessResultsVideo,
  ReadinessStrategyCall,
  useNovaTrialFinePrint,
} from "~/components/readiness/readiness-offer";
import {
  accountReadinessDraftKey,
  ReadinessQuestionnaire,
} from "~/components/readiness/readiness-questionnaire";
import {
  ReadinessComponentCards,
  ReadinessSummary,
  useReadinessView,
} from "~/components/readiness/readiness-score";
import { useReadinessQuestionEvents } from "~/components/readiness/use-readiness-question-events";
import { Button, ButtonRouter } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import IcfBadgeUrl from "~/img/icfbp-logo.svg";
import RemiVogelPhotoUrl from "~/img/readiness/remi-vogel.jpg";
import StephanieEscorialPhotoUrl from "~/img/readiness/stephanie-escorial.jpg";
import { isAuthError } from "~/lib/auth/auth-error";
import { cn } from "~/lib/cn";
import {
  completeMyReadinessFn,
  completePublicReadinessFn,
  getPublicReadinessFn,
  startMyReadinessFn,
  startPublicReadinessFn,
  startReadinessTrialFn,
} from "~/lib/functions/readiness";
import {
  trackReadinessEmailSubmitted,
  trackReadinessResultRevealed,
  trackReadinessStarted,
  trackReadinessViewed,
} from "~/lib/monitoring/track-event";
import { MY_READINESS_QUERY_KEY } from "~/lib/queries/readinessQuery";
import { BRAND_URL } from "~/lib/shared/brand";
import { createMetaTags } from "~/lib/shared/meta";
import { READINESS_SCORE_NAME } from "~/lib/shared/readiness/config";
import type { ReadinessResult } from "~/lib/shared/readiness/engine";
import { READINESS_RERUN_NEEDS_PAID_PLAN_MESSAGE } from "~/lib/shared/readiness/rerun";
import {
  READINESS_PAGE_PATH,
  READINESS_PUBLIC_PATH,
} from "~/lib/shared/readiness/routes";
import { UserFacingError } from "~/lib/shared/user-facing-error";

const searchSchema = z.object({
  /** The emailed link had expired or was not recognised. */
  link: z.enum(["expired"]).optional().catch(undefined),
  /** She came back from email verification to start the trial. */
  next: z.enum(["trial"]).optional().catch(undefined),
  /** An ad link opens the email step, not the pitch. */
  start: z.enum(["email"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_public/6-figure-readiness-score")({
  staticData: { title: READINESS_SCORE_NAME },
  validateSearch: searchSchema,
  loader: async ({ context }) => {
    const readiness = await getPublicReadinessFn();
    // A returning visitor opens on her result, so SSR places its video. The
    // prefetch only hands SSR the answer: a failed read is a result without it.
    if (readiness?.result) {
      await context.queryClient
        .ensureQueryData(READINESS_RESULTS_VIDEO_QUERY)
        .catch(() => undefined);
    }
    return readiness;
  },
  head: () =>
    createMetaTags({
      title: `${READINESS_SCORE_NAME} | Dream Coach Match`,
      description:
        "Answer 16 quick questions and see how to get to $100K a year as a coach: which of the six components you have, and which one to build first.",
      canonical: `${BRAND_URL}${READINESS_PUBLIC_PATH}`,
      ogUrl: `${BRAND_URL}${READINESS_PUBLIC_PATH}`,
    }),
  component: ReadinessFunnelPage,
});

/**
 * `account_questions`: a signed-in visitor skips the email step. Her attempt
 * belongs to her account from the start, and her result opens on her score
 * page, which knows her plan.
 */
type Step = "landing" | "email" | "questions" | "account_questions" | "result";

const emailStepSchema = z.object({
  firstName: z.string().trim().min(1, "Enter your first name").max(80),
  email: z.string().trim().email("Enter a valid email address").max(254),
});
type Contact = z.infer<typeof emailStepSchema>;

const DRAFT_KEY = "readiness-draft:public";

/**
 * Why a signed-in start did not open the questions. `not_due` and
 * `needs_paid_plan` mean she already has a score, so both start buttons turn
 * into a way to it, with when she can rerun underneath.
 */
type StartNotice =
  | { kind: "not_due"; dueAt: Date }
  | { kind: "needs_paid_plan" }
  | { kind: "error"; message: string };

const DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** Two coaches on the assessment; Marcel confirmed both quotes and photos. */
const TESTIMONIALS = [
  {
    name: "Stephanie Escorial",
    photo: StephanieEscorialPhotoUrl,
    quote:
      "I'd been stuck for weeks on where to go, and now I can just run with it.",
  },
  {
    name: "Remi Vogel",
    photo: RemiVogelPhotoUrl,
    quote:
      "In just two questions, it put me in front of an inconsistency between what I wanted to do and what I was actually doing.",
  },
] as const;

const CONTAINER = "mx-auto w-full max-w-6xl px-4";
const SECTION_Y = "py-14 md:py-20";
const SECTION_HEADING =
  "font-heading text-3xl font-medium tracking-tight text-balance text-gray-900 sm:text-4xl";

function ReadinessFunnelPage(): React.JSX.Element {
  const initial = Route.useLoaderData();
  const { link, next, start } = Route.useSearch();
  const [result, setResult] = useState<ReadinessResult | null>(
    initial?.result ?? null,
  );
  const [step, setStep] = useState<Step>(
    initial?.result
      ? "result"
      : initial
        ? "questions"
        : start === "email"
          ? "email"
          : "landing",
  );
  // A first visit loads before she types these; the trial sign-up prefills them.
  const [contact, setContact] = useState<Contact>({
    firstName: initial?.firstName ?? "",
    email: initial?.email ?? "",
  });
  const topRef = useRef<HTMLDivElement>(null);
  // Until Clerk has loaded, it cannot say whether she is signed in.
  const { isLoaded, isSignedIn, userId } = useAuth();
  const [startNotice, setStartNotice] = useState<StartNotice | null>(null);
  // Whose account the open attempt belongs to. After a sign-out or a switch
  // of account, the page goes back to its pitch.
  const [attemptOwner, setAttemptOwner] = useState<string | null>(null);

  useEffect(() => {
    trackReadinessViewed("public");
  }, []);

  const scrollToTop = useCallback(() => {
    topRef.current?.scrollIntoView({ block: "start" });
  }, []);

  const goTo = useCallback(
    (nextStep: Step) => {
      setStep(nextStep);
      scrollToTop();
    },
    [scrollToTop],
  );

  // The same start, and the same rerun rule, as her score page.
  const startForAccount = useMutation({
    mutationFn: (_owner: string) => startMyReadinessFn(),
    onSuccess: (outcome, owner) => {
      if (outcome.kind === "not_due") {
        setStartNotice({ kind: "not_due", dueAt: new Date(outcome.dueAt) });
        return;
      }
      setStartNotice(null);
      setAttemptOwner(owner);
      trackReadinessStarted("signed_in");
      goTo("account_questions");
    },
    onError: (error) => {
      if (
        error instanceof UserFacingError &&
        error.message === READINESS_RERUN_NEEDS_PAID_PLAN_MESSAGE
      ) {
        setStartNotice({ kind: "needs_paid_plan" });
        return;
      }
      setStartNotice({
        kind: "error",
        message:
          error instanceof UserFacingError
            ? error.message
            : "We couldn't start the assessment. Please try again.",
      });
      scrollToTop();
    },
  });

  // A signed-in coach has no email step, so an ad link shows her the pitch.
  const showLanding =
    step === "landing" ||
    (step === "email" && isSignedIn === true) ||
    (step === "account_questions" && userId !== attemptOwner);

  return (
    <div ref={topRef}>
      {link === "expired" ? (
        <div className="px-4 pt-8">
          <p
            role="status"
            className="mx-auto max-w-2xl rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            That link has expired. Take the assessment again to see your result;
            it takes about two minutes.
          </p>
        </div>
      ) : null}
      {showLanding ? (
        <Landing
          starting={startForAccount.isPending || !isLoaded}
          notice={startNotice}
          onStart={() => {
            if (!isLoaded) return;
            if (isSignedIn) startForAccount.mutate(userId);
            else goTo("email");
          }}
        />
      ) : step === "result" && result ? (
        <PublicResult
          result={result}
          firstName={contact.firstName}
          email={contact.email}
          startTrialOnLoad={next === "trial"}
        />
      ) : (
        <div className={cn(CONTAINER, "py-8 sm:py-12")}>
          {step === "email" ? (
            <EmailStep
              onBack={start === "email" ? () => goTo("landing") : undefined}
              onStarted={(submitted) => {
                setContact(submitted);
                goTo("questions");
              }}
            />
          ) : step === "account_questions" && userId ? (
            <AccountQuestions key={userId} clerkUserId={userId} />
          ) : step === "questions" ? (
            <PublicQuestions
              onResult={(completed) => {
                setResult(completed);
                goTo("result");
              }}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function StartButton({
  starting,
  notice,
  centered = false,
  onStart,
}: {
  starting: boolean;
  notice: StartNotice | null;
  centered?: boolean;
  onStart: () => void;
}): React.JSX.Element {
  if (notice?.kind === "not_due" || notice?.kind === "needs_paid_plan") {
    return (
      <div
        className={cn(
          "flex flex-col gap-3 max-sm:items-stretch",
          centered ? "sm:items-center" : "sm:items-start",
        )}
      >
        <ButtonRouter
          variant="primary"
          size="lg"
          to={READINESS_PAGE_PATH}
          className="max-sm:w-full"
        >
          Go to my score
          <ArrowRight aria-hidden="true" />
        </ButtonRouter>
        <p role="status" className="text-text-secondary text-sm">
          {notice.kind === "not_due"
            ? `You can rerun your score from ${DATE_FORMAT.format(notice.dueAt)}.`
            : READINESS_RERUN_NEEDS_PAID_PLAN_MESSAGE}
        </p>
      </div>
    );
  }
  return (
    <Button
      variant="primary"
      size="lg"
      loading={starting}
      onClick={onStart}
      className="max-sm:w-full"
    >
      Start free assessment
      <ArrowRight aria-hidden="true" />
    </Button>
  );
}

function Landing({
  starting,
  notice,
  onStart,
}: {
  starting: boolean;
  /** What her signed-in start answered, shown at both start buttons. */
  notice: StartNotice | null;
  onStart: () => void;
}): React.JSX.Element {
  return (
    <div className="bg-white">
      <div className={CONTAINER}>
        <section className="grid items-center gap-10 pt-10 pb-10 sm:pt-16 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
          <div>
            <p className="text-xs font-medium tracking-[0.14em] text-purple-700 uppercase">
              {READINESS_HERO.eyebrow}
            </p>
            <h1 className="mt-4 font-heading text-4xl leading-[1.08] font-medium tracking-tight text-balance text-gray-900 sm:text-5xl lg:text-[3.5rem]">
              {READINESS_HERO.title}
              <span className="mt-3 block text-[0.58em] leading-tight tracking-normal text-purple-700">
                {READINESS_HERO.subtitle}
              </span>
            </h1>
            <p className="text-text-secondary mt-6 max-w-[52ch] text-lg leading-relaxed text-pretty sm:text-xl">
              {READINESS_HERO.intro}
            </p>
            <div className="mt-8">
              <StartButton
                starting={starting}
                notice={notice}
                onStart={onStart}
              />
            </div>
            {notice?.kind === "error" ? (
              <p role="alert" className="mt-3 text-sm text-red-700">
                {notice.message}
              </p>
            ) : notice ? null : (
              <p className="text-text-tertiary mt-3 text-sm">
                Takes two minutes, no typing. Free, no card.
              </p>
            )}
          </div>
          <ReadinessPreviewCard />
        </section>

        <aside className="grid items-center gap-4 rounded-2xl border border-purple-200 bg-linear-135 from-white to-purple-50 p-6 sm:grid-cols-[auto_1fr] sm:gap-8 sm:px-8">
          <img
            src={IcfBadgeUrl}
            alt="International Coaching Federation Business Solutions Partner badge"
            width={205}
            height={65}
            className="h-16 w-auto sm:h-20"
          />
          <div>
            <p className="font-heading text-xl leading-snug font-medium text-gray-900 sm:text-2xl">
              Dream Coach Match is an official business partner of the ICF
            </p>
            <p className="text-text-secondary mt-2 text-sm text-pretty sm:text-base">
              The International Coaching Federation is the world's largest
              network of trained coaching professionals, with over 55,000
              certified coaches across 158 countries.
            </p>
          </div>
        </aside>
      </div>

      <section className={cn(CONTAINER, SECTION_Y)}>
        <h2 className={SECTION_HEADING}>{READINESS_COMPONENTS_INTRO.title}</h2>
        <p className="text-text-secondary mt-4 max-w-[60ch] text-lg text-pretty">
          {READINESS_COMPONENTS_INTRO.body}
        </p>
        <ReadinessComponentTiles className="mt-10" />
      </section>

      <section className={cn("bg-purple-50", SECTION_Y)}>
        <div
          className={cn(
            CONTAINER,
            "grid items-center gap-8 lg:grid-cols-[1fr_1.5fr] lg:gap-12",
          )}
        >
          <h2 className={SECTION_HEADING}>{READINESS_STEPS_TITLE}</h2>
          {/* The dashed line runs behind the cards, through their numbers. */}
          <ReadinessSteps />
        </div>
      </section>

      <section className={cn(CONTAINER, SECTION_Y)}>
        <h2 className={cn(SECTION_HEADING, "text-center")}>What people say</h2>
        <div className="mx-auto mt-10 grid max-w-4xl gap-5 md:grid-cols-2">
          {TESTIMONIALS.map((testimonial) => (
            <figure
              key={testimonial.name}
              className="grid grid-cols-[6.5rem_1fr] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm sm:min-h-48 sm:grid-cols-[8.75rem_1fr]"
            >
              <div className="relative bg-purple-100">
                <img
                  src={testimonial.photo}
                  alt=""
                  width={320}
                  height={320}
                  loading="lazy"
                  className="absolute inset-0 size-full object-cover object-[center_15%]"
                />
              </div>
              <div className="flex flex-col justify-between gap-3 p-4 sm:p-6">
                <blockquote className="font-heading text-[15px] leading-normal font-medium text-gray-900 sm:text-base">
                  “{testimonial.quote}”
                </blockquote>
                <figcaption className="text-sm font-medium text-gray-900">
                  {testimonial.name}
                </figcaption>
              </div>
            </figure>
          ))}
        </div>
      </section>

      <section className={cn("bg-purple-50 text-center", SECTION_Y)}>
        <div className={CONTAINER}>
          <h2 className={SECTION_HEADING}>See what is holding you back</h2>
          <p className="text-text-secondary mx-auto mt-4 max-w-xl text-lg text-pretty">
            Most coaches spend a year working on the wrong thing. Two minutes
            now shows you what to build first, for your situation.
          </p>
          <div className="mt-8">
            <StartButton
              starting={starting}
              notice={notice}
              centered
              onStart={onStart}
            />
          </div>
          {notice ? null : (
            <p className="text-text-tertiary mt-4 text-sm">
              Free. No card. Only you and the Dream Coach Match team can see
              your answers.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

function EmailStep({
  onBack,
  onStarted,
}: {
  onBack?: () => void;
  onStarted: (contact: Contact) => void;
}): React.JSX.Element {
  const [attempted, setAttempted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useAppForm({
    defaultValues: { firstName: "", email: "" },
    onSubmit: async ({ value }) => {
      setError(null);
      const parsed = emailStepSchema.safeParse(value);
      if (!parsed.success) return;
      try {
        await startPublicReadinessFn({ data: parsed.data });
        trackReadinessEmailSubmitted();
        trackReadinessStarted("public");
        onStarted(parsed.data);
      } catch (startError) {
        setError(
          startError instanceof UserFacingError
            ? startError.message
            : "We couldn't start the assessment. Please try again.",
        );
      }
    },
  });
  const values = useStore(form.store, (state) => state.values);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const parsed = emailStepSchema.safeParse(values);
  const errorFor = (field: string): string | undefined =>
    attempted && !parsed.success
      ? parsed.error.issues.find((issue) => issue.path[0] === field)?.message
      : undefined;

  return (
    <Card
      variant="flat"
      className="mx-auto w-full max-w-md rounded-3xl border border-gray-200 shadow-xl shadow-purple-950/5 sm:my-8 sm:p-8"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          setAttempted(true);
          void form.handleSubmit();
        }}
      >
        <div>
          <p className="text-text-tertiary text-sm">
            Your answers are private to you and the Dream Coach Match team. We
            use them for your result and to help you grow your practice.
          </p>
          <h1 className="mt-3 font-heading text-2xl font-medium tracking-tight text-gray-900 sm:text-[1.625rem]">
            Where should we send your results?
          </h1>
        </div>
        <FieldErrorsProvider value={errorFor}>
          <form.AppField name="firstName">
            {(field) => (
              <field.TextField label="First name" placeholder="Anna" required />
            )}
          </form.AppField>
          <form.AppField name="email">
            {(field) => (
              <field.TextField
                label="Email"
                type="email"
                placeholder="anna@example.com"
                required
              />
            )}
          </form.AppField>
        </FieldErrorsProvider>
        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={isSubmitting}
          className="mt-2 w-full"
        >
          Start
        </Button>
        <p className="text-text-tertiary text-center text-sm">
          16 questions, about two minutes.
        </p>
        {onBack ? (
          <Button type="button" variant="ghost" onClick={onBack}>
            Back
          </Button>
        ) : null}
      </form>
    </Card>
  );
}

function PublicQuestions({
  onResult,
}: {
  onResult: (result: ReadinessResult) => void;
}): React.JSX.Element {
  const { onViewed, onAnswered } = useReadinessQuestionEvents("public");
  const queryClient = useQueryClient();

  // Her result opens with its video already known, so the page does not jump.
  useEffect(() => {
    void queryClient.prefetchQuery(READINESS_RESULTS_VIDEO_QUERY);
  }, [queryClient]);

  return (
    <ReadinessQuestionnaire
      draftKey={DRAFT_KEY}
      onQuestionViewed={onViewed}
      onQuestionAnswered={onAnswered}
      onSubmit={async (answers) => {
        const result = await completePublicReadinessFn({ data: { answers } });
        trackReadinessResultRevealed({
          surface: "public",
          band: result.band,
          holding_back: result.holdingBack,
        });
        onResult(result);
      }}
    />
  );
}

function AccountQuestions({
  clerkUserId,
}: {
  clerkUserId: string;
}): React.JSX.Element {
  const { onViewed, onAnswered } = useReadinessQuestionEvents("signed_in");
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return (
    <ReadinessQuestionnaire
      draftKey={accountReadinessDraftKey(clerkUserId)}
      onQuestionViewed={onViewed}
      onQuestionAnswered={onAnswered}
      onSubmit={async (answers) => {
        const outcome = await completeMyReadinessFn({ data: { answers } });
        if (outcome.kind === "not_due") {
          throw new UserFacingError(
            `You can rerun your score from ${DATE_FORMAT.format(new Date(outcome.dueAt))}.`,
          );
        }
        if (outcome.kind === "not_started") {
          throw new UserFacingError(
            "This assessment timed out. Start it again to see your result.",
          );
        }
        trackReadinessResultRevealed({
          surface: "signed_in",
          band: outcome.result.band,
          holding_back: outcome.result.holdingBack,
        });
        // Her score page loads these on arrival. Drop what an earlier visit
        // cached: her runs, NOVA's `hasScore`, and, for someone the start just
        // made a coach, the coach area's "no coach" answer and her account.
        for (const queryKey of [
          MY_READINESS_QUERY_KEY,
          NOVA_SUBSCRIPTION_QUERY_KEY,
          ["my-coaching-profile"],
          ["coach-account"],
        ]) {
          queryClient.removeQueries({ queryKey });
        }
        await navigate({ to: READINESS_PAGE_PATH });
      }}
    />
  );
}

function PublicResult({
  result,
  firstName,
  email,
  startTrialOnLoad,
}: {
  result: ReadinessResult;
  firstName: string;
  email: string;
  startTrialOnLoad: boolean;
}): React.JSX.Element {
  const view = useReadinessView(result);
  const router = useRouter();
  const trialFine = useNovaTrialFinePrint();
  const [verifying, setVerifying] = useState(false);
  const [trialError, setTrialError] = useState<string | null>(null);
  const trial = useMutation({ mutationFn: () => startReadinessTrialFn() });
  const { mutateAsync } = trial;

  const startTrial = useCallback(async () => {
    setTrialError(null);
    try {
      const { url } = await mutateAsync();
      window.location.assign(url);
    } catch (error) {
      if (isAuthError(error)) {
        // Not signed in yet: she confirms her email first, then comes back.
        setVerifying(true);
        return;
      }
      setTrialError("We couldn't start your trial. Please try again.");
    }
  }, [mutateAsync]);

  useEffect(() => {
    if (startTrialOnLoad) void startTrial();
  }, [startTrialOnLoad, startTrial]);

  const trialButton = (
    <Button
      variant="primary"
      size="lg"
      loading={trial.isPending}
      onClick={() => void startTrial()}
    >
      Start my free 7-day trial
    </Button>
  );

  // Both trial buttons land here: the sign-up and any error sit above the
  // offer, and come into view when they appear.
  const trialNoticeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (verifying || trialError) {
      trialNoticeRef.current?.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    }
  }, [verifying, trialError]);

  const errorNote = trialError ? (
    <p role="alert" className="text-center text-sm text-red-700">
      {trialError}
    </p>
  ) : null;

  const verifyPanel = verifying ? (
    <Card className="mx-auto w-full max-w-md">
      <AuthFlow
        signupMethod="email_link"
        defaultEmail={email || undefined}
        defaultFirstName={firstName || undefined}
        emailStepTitle="Confirm your email to start your trial"
        emailStepDescription="Your result and your trial go in the same free account."
        redirectUrl={`${READINESS_PUBLIC_PATH}?next=trial`}
        onSuccess={() => {
          setVerifying(false);
          void router.invalidate();
          void startTrial();
        }}
        onError={setTrialError}
      />
    </Card>
  ) : null;

  return (
    <div className="bg-white">
      <div className={cn(CONTAINER, "pt-8 sm:pt-12")}>
        <Card className="rounded-2xl shadow-lg shadow-purple-950/5">
          <ReadinessSummary result={result} headingLevel="h2" />
        </Card>
      </div>
      {view.isPastSixFigures ? null : (
        <ReadinessResultsVideo
          action={trialButton}
          fine={trialFine}
          className={CONTAINER}
        />
      )}
      <ReadinessComponentCards
        result={result}
        className={cn(CONTAINER, SECTION_Y)}
      />
      {verifyPanel || errorNote ? (
        <div
          ref={trialNoticeRef}
          className={cn(CONTAINER, "flex flex-col gap-4 pb-14 md:pb-20")}
        >
          {verifyPanel}
          {errorNote}
        </div>
      ) : null}
      <section className={cn("bg-purple-50", SECTION_Y)}>
        <div className={CONTAINER}>
          {view.isPastSixFigures ? (
            <ReadinessStrategyCall
              trialAction={
                <Button
                  variant="link"
                  className="h-auto p-0 font-medium text-purple-700 underline"
                  onClick={() => void startTrial()}
                >
                  Start the free 7-day trial, with the Business Builder Program
                  and NOVA AI.
                </Button>
              }
            />
          ) : (
            <ReadinessNovaOffer
              startWith={view.startWith}
              action={trialButton}
              fine={trialFine}
              offersStrategyCall={view.offersStrategyCall}
            />
          )}
        </div>
      </section>
      <p
        className={cn(
          CONTAINER,
          "text-text-tertiary max-w-xl py-8 text-center text-sm text-pretty",
        )}
      >
        We've emailed you a link to your free account, so you can come back to
        your result any time.
      </p>
    </div>
  );
}
